/**
 * Centralized toast description registry.
 *
 * Maps toast title patterns → helpful descriptions that explain what happened
 * and what the user can do. Looked up automatically by showUnifiedToast when
 * no explicit description is provided.
 *
 * Patterns ending with * are prefix-matched (for dynamic titles).
 * All other patterns are exact-matched (case-sensitive).
 */

const TOAST_DESCRIPTIONS: Record<string, string> = {
  // =========================================================================
  // Recording
  // =========================================================================
  "Recording consent confirmed - starting recording":
    "The caller has verbally agreed to be recorded. Recording will begin automatically.",
  "Failed to auto-start recording. Try manually.":
    "The system couldn't start recording after consent was detected. Click the recording toggle in the header to start manually.",
  "Error starting recording. Try manually.":
    "An error occurred while initializing the recording service. Try clicking the recording toggle again.",
  "Recording started*":
    "All audio and video streams are now being captured. You can stop recording at any time from the header controls.",
  "Recording is already active":
    "No action needed — recording was already running when you tried to start it.",
  "Failed to start recording:*":
    "The recording service returned an error. Check that LiveKit Egress is running, then try toggling the recording switch again.",
  "An unexpected error occurred while starting recording.":
    "Something went wrong starting the recording. Try refreshing the page and toggling recording again.",
  "Cannot toggle recording: Call ID is missing.":
    "The call hasn't fully initialized yet. Wait a few seconds for the call to connect, then try again.",
  "Checking for recording consent...":
    "Analyzing the conversation to detect if the officer has asked about recording and the caller has agreed.",
  "Recording stopped.":
    "The recording has been saved. It will be available for playback on the incident report page after the call ends.",
  "Failed to stop recording:*":
    "The recording service couldn't stop the recording. It may stop automatically when the call ends.",
  "An unexpected error occurred while stopping recording.":
    "Something went wrong stopping the recording. The recording may stop automatically when the call ends.",
  "This meeting is being recorded":
    "Another participant has started recording this call. All audio and video will be captured.",

  // =========================================================================
  // AI Caller
  // =========================================================================
  "AI caller disconnected unexpectedly. You can try adding it again.":
    "The AI caller's connection was lost, possibly due to a network issue or server restart. Click the AI Caller button in the header to dispatch a new one.",
  "Cannot toggle AI caller: Call ID is missing.":
    "The call hasn't fully initialized yet. Wait a few seconds for the call to connect, then try again.",
  "AI Caller removed":
    "The AI caller has been disconnected from the call. You can add a new one at any time using the AI Caller button.",
  "Failed to remove AI caller":
    "The server couldn't disconnect the AI caller. Try again, or the AI caller will disconnect when the call ends.",
  "Timed out removing AI caller":
    "The removal request took too long. The AI caller may still disconnect shortly, or try removing it again.",
  "An unexpected error occurred.":
    "Something went wrong. If the problem persists, try refreshing the page.",
  "AI Caller lost voice connection. Attempting to reconnect...":
    "The AI caller's audio stream was interrupted. The system is trying to re-establish the connection automatically.",

  // =========================================================================
  // Auto-Response
  // =========================================================================
  "Auto-response triggered":
    "The system automatically spoke a suggested action to the caller via text-to-speech.",
  "Auto-response mode requires admin access":
    "Only administrators can enable auto-response mode. Contact your system administrator if you need this feature.",
  "Auto-response mode enabled":
    "The system will now automatically speak top suggested actions to the caller after each pause in conversation.",
  "Auto-response mode disabled":
    "The system will no longer automatically speak suggested actions. You can re-enable it from the header toggle.",

  // =========================================================================
  // TTS / Speak Actions
  // =========================================================================
  "Cannot speak: call not connected":
    "The text-to-speech system can't send audio because the LiveKit connection was lost. Check your network and try refreshing.",
  "Speak failed:*":
    "The text-to-speech service couldn't process this action. Try again, or read the action aloud manually.",
  "Speak error - please try again":
    "The text-to-speech service encountered an error. Wait a moment and try speaking the action again.",
  "Officer assist is still starting. Queued asks are paused until it joins.":
    "The officer-assist TTS agent hasn't joined the call yet. It should connect within 10-15 seconds. Queued actions will be spoken once it's ready.",
  "Officer assist is taking longer than usual to join. Queued asks will resume when it connects.":
    "The officer-assist TTS agent didn't join within 45s — usually means the worker is at capacity from another session. The queue will auto-resume the moment it connects; no action needed unless this persists across multiple calls (in which case restart the officer-assist deployment).",

  // =========================================================================
  // Action Verification
  // =========================================================================
  "Unable to verify the answer right now. The action was not removed.":
    "The AI couldn't confirm whether this action was answered in the conversation. The action card stays visible so you can review it manually.",
  "Unable to generate actions: missing call ID":
    "The call session isn't fully initialized. Wait for the call to connect, then try again.",
  "Unable to generate actions: no transcript available":
    "There's no transcript data to analyze yet. Wait for some conversation to occur before requesting more actions.",
  "Failed to generate more actions for this group":
    "The AI engine couldn't produce additional actions. This may be due to rate limiting — try again in a few seconds.",

  // =========================================================================
  // Wrap-Up
  // =========================================================================
  "Wrap-up queued for officer":
    "A wrap-up notification has been sent to the officer. They will see it in their header controls.",
  "Failed to send wrap-up signal":
    "The wrap-up notification couldn't be delivered. Try sending it again from the header menu.",

  // =========================================================================
  // AI Scenario Generation
  // =========================================================================
  "AI generation unavailable — using a sample scenario instead":
    "The AI scenario generator couldn't be reached. A pre-built sample persona will be used instead. You can select a different persona manually.",

  // =========================================================================
  // File Operations
  // =========================================================================
  "Failed to delete files":
    "One or more files couldn't be deleted from the knowledge base. Check your permissions and try again.",
  "Failed to reprocess files":
    "The reprocessing request failed. The files may still be in their previous state. Try again later.",
  "Failed to enable files":
    "The selected files couldn't be enabled. Check your permissions and try again.",
  "Failed to disable files":
    "The selected files couldn't be disabled. Check your permissions and try again.",
  "Version restored successfully":
    "The document has been reverted to the selected version. The current version is now the restored one.",
  "Failed to restore version":
    "The version restore failed. The document remains at its current version. Try again.",
  "This version does not have a downloadable file.":
    "This version entry is metadata-only — there's no file attachment to download.",
  "Document reprocessing started":
    "The document is being re-chunked and re-vectorized. This may take a minute depending on document size.",
  "Document deleted":
    "The document and all its versions have been permanently removed from the knowledge base.",

  // =========================================================================
  // File Sharing (in-call)
  // =========================================================================
  "Shared*with caller":
    "The selected files are now visible to the caller in their file browser view.",
  "Unshared*from caller":
    "The selected files are no longer visible to the caller.",

  // =========================================================================
  // Document State
  // =========================================================================
  "Document enabled successfully":
    "The document is now active and will appear in knowledge base search results.",
  "Document disabled successfully":
    "The document has been deactivated and will not appear in search results until re-enabled.",

  // =========================================================================
  // Incident Report / Archive
  // =========================================================================
  "Feedback submitted":
    "Your feedback has been recorded and will be used to improve AI accuracy.",
  "Failed to submit feedback":
    "The feedback couldn't be saved. Check your connection and try again.",
  "Failed to generate report:*":
    "The report generation service encountered an error. Try selecting a different template or refreshing the page.",
  "Report PDF uploaded to file browser":
    "The generated PDF report has been saved to the call's file browser for archival.",
  "PDF upload failed:*":
    "The PDF was generated but couldn't be uploaded to the file browser. You can still download it directly.",
  "Failed to upload PDF to file browser":
    "The file browser service couldn't accept the upload. Try downloading the PDF manually instead.",
  "Call data and report saved successfully!":
    "All call data — transcripts, entities, AI analysis, and the report — have been saved to the cloud.",
  "Failed to save to cloud. See console for details.":
    "The cloud save operation failed. Open the browser console (F12) for specific error details. Your data may still be saved locally.",
  "Failed to copy to clipboard. Please try again.":
    "The browser's clipboard API returned an error. Try clicking the copy button again, or select the text manually.",
  "Failed to load call data from the database.":
    "The call record couldn't be retrieved from Supabase. The call may not exist, or there may be a connection issue.",
  "Archiving call data...":
    "Saving transcripts, chat messages, AI analysis, notes, and feedback to the database.",
  "Archive incomplete:*":
    "Some files couldn't be uploaded during archiving. The call data was saved, but some attachments may be missing.",
  "Call data archived successfully":
    "All transcripts, entities, and AI analysis have been saved. You can view them on the incident report page.",
  "Archive failed:*":
    "The archiving process encountered an error. Try refreshing the page and archiving again from the call.",
  "No call data found.*":
    "The connection may have been lost before any data was saved. Try refreshing the page to attempt recovery.",

  // =========================================================================
  // Deep Analysis
  // =========================================================================
  "No call ID available for analysis.*":
    "The system is still resolving the call's unique identifier. Wait a few seconds and try running the analysis again.",
  "Deep Analysis failed*":
    "The AI couldn't complete the final assessment. Individual component scores may still be available in the results panel.",

  // =========================================================================
  // Reports
  // =========================================================================
  "Failed to generate download link.":
    "The download URL couldn't be created. Try refreshing the page and clicking download again.",

  // =========================================================================
  // Login / Auth
  // =========================================================================
  "Authenticated successfully":
    "You've been signed in. Redirecting to the dashboard.",
  "Logged in successfully":
    "You've been signed in with email and password. Redirecting to the dashboard.",

  // =========================================================================
  // Admin - Organizations
  // =========================================================================
  "Failed to delete organization":
    "The organization couldn't be removed. It may have active users or child organizations that must be removed first.",

  // =========================================================================
  // Admin - Levels
  // =========================================================================
  "Failed to delete level type":
    "The level type couldn't be removed. It may be in use by existing organization levels.",

  // =========================================================================
  // Admin - Repositories
  // =========================================================================
  "Failed to load users":
    "The user list couldn't be retrieved. Check your connection and try refreshing the page.",
  "Failed to load users:*":
    "The user list couldn't be retrieved. The server returned an error — try refreshing.",
  "Failed to reassign repository":
    "The repository ownership transfer failed. Verify the target user has appropriate permissions.",
  "Failed to reassign:*":
    "The repository ownership transfer failed. The server returned an error.",
  "Failed to delete repository":
    "The repository couldn't be removed. It may contain active documents that need to be archived first.",
  "Failed to delete:*":
    "The delete operation failed. The server returned an error — try again.",

  // =========================================================================
  // Settings
  // =========================================================================
  "Setting saved successfully":
    "Your change has been applied. Some settings may require a page refresh to take effect.",
  "Failed to save setting":
    "The setting couldn't be updated. Check your permissions and try again.",
  "Setting reset to inherited value":
    "The setting has been reverted to the value inherited from the parent organization or system default.",
  "Failed to reset setting":
    "The setting couldn't be reset. Check your permissions and try again.",

  // =========================================================================
  // Citizen Survey
  // =========================================================================
  "Thank you for your feedback!":
    "Your ratings and comments have been recorded. They help us improve our service.",
  "Failed to submit feedback. Please try again.":
    "The survey response couldn't be saved. Check your connection and try submitting again.",

  // =========================================================================
  // PDF Generation
  // =========================================================================
  "Failed to generate PDF:*":
    "The PDF rendering service encountered an error. Try a different template or check that report content isn't empty.",
};

/**
 * Look up a description for a toast title.
 * Returns undefined if no match found (caller can fall back to no description).
 */
export function getToastDescription(title: string): string | undefined {
  // Exact match first (fast path)
  if (TOAST_DESCRIPTIONS[title]) {
    return TOAST_DESCRIPTIONS[title];
  }

  // Prefix match for dynamic titles (patterns ending with *)
  for (const [pattern, desc] of Object.entries(TOAST_DESCRIPTIONS)) {
    if (pattern.endsWith('*') && title.startsWith(pattern.slice(0, -1))) {
      return desc;
    }
  }

  return undefined;
}
